/* eslint-disable @typescript-eslint/no-explicit-any */
import * as types from '../constants/actionTypes';
import initialState from './initialState';
import { parseFromAPIServer } from '../functions/messageUtils';
import * as messageStatus from '../constants/messageStatus';
import * as chatTypes from '../constants/chatTypes';
import { TYPE_GROUP_CHAT } from '../constants/chatTypes';
import { datetimeToTimeStamp, isMsgExpired } from '../functions/utils';
import Immutable, { ImmutableObject } from 'seamless-immutable';
import cloneDeep from 'lodash.clonedeep';
import * as myStore from '../store/myStore';
import * as messageTypes from '../constants/messageTypes';
import {
  TYPE_INVITE_GROUP_CHAT,
  TYPE_JOINED_GROUP_CHAT,
  TYPE_LEAVE_GROUP_CHAT,
  TYPE_REMOVED_FROM_GROUP,
  TYPE_SHARE_EVENT_TICKET,
  TYPE_SHARE_EVENT,
  TYPE_INVITE_GROUP,
} from '../constants/messageTypes';
import { CHAT_TYPES } from '../constants/constants';
import ChatMessage from 'redhotpie-shared-lib/models/chatMessage';

type MessageState = {
  isError: boolean;
  isLoading: boolean;
  action: {
    type: string;
    message: any;
    oldMessage: any;
    updatedTime: number;
    lastedInviteMessageInReducer?: ChatMessage;
  };
  groupAction: {
    type: string;
    message: any;
    oldMessage: any;
    updatedTime: number;
  };
  byId: Record<string, ChatMessage>;
  chat: Record<string, string[]>;
  groupchat: Record<string, string[]>;
  // heartMsg: Record<string, any>;
  lastUpdateMessage: number;
  messageWaitToSend: Record<string, ChatMessage>;
  messagesInvitePrivateGroup: ChatMessage[];
  profilePhotoCommentShouldBeSend: ChatMessage | null;
  lastedMessageInviteGroupChatByUser: Record<string, Record<string, ChatMessage>>;
  lastedMessageShareEventByUser: Record<string, Record<string, ChatMessage>>;
};

export type MessageReducerAction = {
  type: string;
  message: ChatMessage;
  mid: string;
  status: string;
  bodyType: string;
  messages: ChatMessage[];
  imageData: any[];
  peerXmppId: string;
  galleryId: string;
  loginId: string;
  user: any;
  fromJid: string;
  unicode: string;
  userId: string | number;
  isClearReaction: boolean;
  roomJid: string;
  data: any;
  shouldRemoveOldMessages: boolean;
};

const messageInitialState: ImmutableObject<MessageState> = Immutable(initialState.message);

const MESSAGES_INVITE_PRIVATE_GROUP_CHAT = [TYPE_SHARE_EVENT_TICKET, TYPE_INVITE_GROUP_CHAT];

function isActiveInviteMessageGroupChat(message: ChatMessage): boolean {
  return message?.body?.type === TYPE_INVITE_GROUP_CHAT && !message.isDelete && !isMsgExpired(message);
}

function isActiveMessageTicketLink(message: ChatMessage): boolean {
  return message?.body?.type === TYPE_SHARE_EVENT_TICKET && !message.isDelete && !isMsgExpired(message);
}

function getLoginXmppId(): string {
  const login = myStore.getStore().getState().loginReducer;
  if (login && login.userInfo) {
    return login.userInfo.xmppId;
  }
  return '';
}

function removeUserReactionsFromMsg(msg: ChatMessage | null, userId: string | number): ChatMessage | null {
  if (!msg) {
    return null;
  }
  const message = cloneDeep(msg);
  if (message.reactions && Object.keys(message.reactions).length > 0) {
    Object.keys(message.reactions).forEach(unicode => {
      const reactionObject = message.reactions?.[unicode];
      if (reactionObject && reactionObject[userId] !== undefined) {
        delete message.reactions![unicode][userId];
      }
    });
  }

  return message;
}

// eslint-disable-next-line
export function messageReducer(
  state: ImmutableObject<MessageState> = messageInitialState,
  action: MessageReducerAction
): ImmutableObject<MessageState> {
  const {
    type,
    message,
    mid = '',
    status = '',
    bodyType = messageTypes.TYPE_TEXT,
    messages,
    imageData = [],
    peerXmppId,
    galleryId,
    loginId,
    user,
    fromJid = '',
    unicode,
    userId,
    isClearReaction,
    roomJid,
    data,
    shouldRemoveOldMessages,
  } = action;
  const actionType = type;
  switch (actionType) {
    case types.UPDATE_MESSAGE_REDUCER: {
      return state.merge({ ...data });
    }
    case types.ADD_MESSAGE_TO_QUEUE_TO_SEND: {
      return state.setIn(['messageWaitToSend', `${userId}`], message);
    }
    case types.REMOVE_MESSAGE_IN_QUEUE_TO_SEND: {
      return state.setIn(['messageWaitToSend', `${userId}`], undefined);
    }
    case types.UPDATE_DELETE_REFERENCE_MESSAGES: {
      const originMessage = state.byId[mid];
      if (originMessage) {
        Object.keys(state.byId).forEach(messageId => {
          const message = state.byId[messageId];
          if (message && message.refMsgId === mid) {
            state = state.setIn(['byId', messageId, 'refMsgIsDeleted'], true);
          }
        });
        const actionObject = {
          type: actionType,
          message: {}, // object _message xmpp _message object in byId,
          oldMessage: {},
          updatedTime: +new Date(),
        };
        state = state.setIn(['groupAction'], actionObject).setIn(['action'], actionObject);
      }
      return state;
    }
    case types.CHAT_UPDATE_MESSAGE_STATUS: {
      const oldMessage = state.byId[mid];
      if (
        status !== messageStatus.STATUS_DELETED &&
        status !== messageStatus.STATUS_OPENED &&
        (state.byId[mid] == null || state.byId[mid].status === messageStatus.STATUS_READ) &&
        status !== messageStatus.STATE_STATUS_DECLINE &&
        status !== messageStatus.STATE_STATUS_ACCEPT &&
        status !== messageStatus.STATE_GROUP_CONFIRMED &&
        status !== null
      ) {
        return state;
      } // if mid not found or status is 'read', don't change to another status
      if (
        status === messageStatus.STATE_STATUS_DECLINE ||
        status === messageStatus.STATE_STATUS_ACCEPT ||
        status === messageStatus.STATE_GROUP_CONFIRMED ||
        status === null
      ) {
        state = state.setIn(['byId', mid, 'state'], status);
      } else if (status === messageStatus.STATUS_OPENED) {
        state = state.setIn(['byId', mid, 'giftOpened'], true);
      } else {
        // case delete
        state = state.setIn(['byId', mid, 'status'], status);
      }
      const _message = state.byId[mid];
      let actionObject = {};
      if (_message.type === TYPE_GROUP_CHAT) {
        actionObject = {
          type: actionType,
          message: _message, // object _message xmpp _message object in byId,
          oldMessage,
          updatedTime: +new Date(),
        };
        state = state.setIn(['groupAction'], actionObject);
      } else {
        actionObject = {
          type: actionType,
          message: _message, // object _message xmpp _message object in byId
        };
        state = state.setIn(['action'], actionObject);
      }
      return state;
    }

    case types.RESET_MESSAGE_TO_INIT_DATA: {
      state = initialState.message;
      return state;
    }

    case types.CHAT_UPDATE_MESSAGE_ERROR: {
      state = state.setIn(['byId', mid, 'error'], status);
      const _message = state.byId[mid];

      const actionObject = {
        type: actionType,
        message: _message, // object _message xmpp _message object in byId
        updatedTime: +new Date(),
      };
      if (_message.type === 'groupchat') {
        state = state.setIn(['groupAction'], actionObject);
      } else {
        state = state.setIn(['action'], actionObject);
      }
      return state;
    }

    case types.CHAT_UPDATE_MESSAGES_STATUS: {
      const messages = state.byId.asMutable() || {};
      let tempMessage = null;
      const updateMessage: any[] = [];
      Object.keys(messages).forEach((key, index) => {
        if (messages[key].type === CHAT_TYPES.CHAT && messages[key].from === peerXmppId) {
          tempMessage = messages[key];
          tempMessage = { ...tempMessage, status: 'deleted' };
          // messages[key] = tempMessage;
          updateMessage.push(tempMessage);
        }
      });
      state = state.set('byId', messages);

      const actionObject = {
        type: actionType,
        message: updateMessage, // object _message xmpp _message object in byId
      };
      state = state.set('action', actionObject);
      return state;
    }

    case types.CHAT_UPDATE_ALBUM_STATUS_SUCCESS: {
      const mid = message.id || message.mid;
      if (state && state.byId && state.byId[mid]) {
        let _message = state.byId[mid].asMutable({ deep: true });
        if (_message && message.body && message.body.data && message.body.data.hasAccess !== undefined) {
          _message.body.data.hasAccess = message.body.data.hasAccess;
          _message.body.data.imageCount = message.body.data.hasAccess ? _message.body.data.imageCount : 0;
          _message.status = message.body.data.hasAccess ? _message.status : messageStatus.STATUS_HIDDEN;
          const actionObject = {
            type: types.CHAT_UPDATE_MESSAGE_STATUS,
            message: _message, // object _message xmpp _message object in byId
          };
          state = state.setIn(['byId', mid], _message);
          state = state.setIn(['action'], actionObject);
        }
      }

      return state;
    }
    case types.CHAT_UPDATE_MESSAGE_FAIL: {
      const { mid } = message;
      return state.setIn(['byId', mid, 'status'], status); // .setIn(['byId', mid, 'time'], +new Date());
    }
    case types.CHAT_REPLACE_MESSAGE_SUCCESS: {
      // const { username = '' } = state.user || {};
      let { type } = message;
      const { id, to, status } = message;
      // info source: if the field from is empty, then assume message is sent by current user
      const from = message.from;
      // current user: mark as send by self
      const bySelf = from === loginId;
      // group or chat room id: sent by self or not one-to-one chat, then it's receiver's ID or sender's ID
      //      let chatId = bySelf || (type !== chatTypes.TYPE_CHAT && type !== 'end') ? to : from;

      const chatMessage = {
        ...message,
        bySelf,
        time: message.time || +new Date(),
        status,
      };
      //      if (!chatMessage.from && actionType === types.CHAT_ADD_SEND_MESSAGE_SUCCESS) {
      //        chatMessage.from = loginId;
      //      }
      //
      //      if (message.body.isPrivate === chatTypes.TYPE_PRIVATE_MSG) {
      //        chatMessage.to = (chatId && chatId.split('/')[1]) || '';
      //      }
      state = state.setIn(['byId', id], chatMessage);
      //      if (message.body.isPrivate && message.body.isPrivate === chatTypes.TYPE_PRIVATE_MSG) {
      //        chatId = (chatId && chatId.split('/')[0]) || '';
      //      }
      //      if (type === 'end') {
      //        type = 'chat';
      //      }
      //      const chatData = state[type] && state[type][chatId] ? state[type][chatId].asMutable() : [];
      //      chatData.push(id);
      //
      //      if (type === chatTypes.TYPE_GROUP_CHAT && chatData.length >= 200) {
      //        state = state.setIn([type, chatId], chatData.slice(50));
      //      } else {
      //        state = state.setIn([type, chatId], chatData);
      //      }
      //      if (message.refMsgShortDesc && message.refMsgShortDesc !== '') {
      //        chatMessage.refMsgShortDesc = message.refMsgShortDesc;
      //      }
      //      if (message.refMsgTitle) {
      //        chatMessage.refMsgTitle = message.refMsgTitle;
      //      }
      //      if (message.body && message.body.type === messageTypes.TYPE_TEXT) {
      //        chatMessage.body.data = message.body.data;
      //      }
      if (type === chatTypes.TYPE_GROUP_CHAT) {
        const groupActionObject = {
          type: actionType,
          message: chatMessage,
        };
        state = state.set('groupAction', groupActionObject);
      } else {
        const actionObject = {
          type: actionType,
          message: chatMessage, // object message xmpp message object in byId
        };
        state = state.set('action', actionObject);
      }
      return state;
    }
    case types.CHAT_ADD_SEND_MESSAGE_SUCCESS:
    case types.CHAT_ADD_RECEIVE_MESSAGE_SUCCESS: {
      const idCurrentMsg = message.id;
      const arrIdMsg = state.byId && Object.keys(state.byId);
      const messagesInvitePrivateGroupChat = [...state.messagesInvitePrivateGroup];
      const isAddMsg = arrIdMsg.length > 0 && arrIdMsg.includes(idCurrentMsg);

      let messageInvite: ChatMessage | undefined = undefined;
      let lastedInviteMessageInReducer: ChatMessage | undefined = undefined;

      if (isAddMsg) {
        return state;
      }
      // if (!message.status) {
      // message = parseFromServer(message, bodyType);
      // }
      // const { username = '' } = state.user || {};
      let { type } = message;
      const { id, to, status } = message;
      // info source: if the field from is empty, then assume message is sent by current user
      const from = message.from;
      // current user: mark as send by self
      const bySelf = from === loginId;
      // group or chat room id: sent by self or not one-to-one chat, then it's receiver's ID or sender's ID
      let chatId = bySelf || (type !== chatTypes.TYPE_CHAT && type !== 'end') ? to : from;

      const chatMessage = {
        ...message,
        bySelf,
        time: message.time || +new Date(),
        status,
      };

      const lastedMessageInviteGroupChatByUser = state.lastedMessageInviteGroupChatByUser || {};
      const lastedMessageShareEventByUser = state.lastedMessageShareEventByUser || {};
      const lastedMessageInviteGroupChatObject = { ...(lastedMessageInviteGroupChatByUser[chatId]?.asMutable({ deep: true }) || {}) };
      const lastedMessageShareEventObject = { ...(lastedMessageShareEventByUser[chatId]?.asMutable({ deep: true }) || {}) };

      if (isValidMessage(chatMessage)) {
        if (isInviteGroupMessage(chatMessage)) {
          messageInvite = chatMessage;
          lastedInviteMessageInReducer = lastedMessageInviteGroupChatObject[messageInvite.body.data.groupId];
        } else if (isShareEventMessage(chatMessage)) {
          messageInvite = chatMessage;
          lastedInviteMessageInReducer = lastedMessageShareEventObject[messageInvite.body.data.id];
        }

        if (lastedInviteMessageInReducer! && type !== 'end') {
          // if lastedInviteMessageInReducer is exist, we remove msg which have same groupId if is
          // inviteGroup of same event id if is shareEvent message (just need to remove 1 message
          // because we already remove them in LOAD_CONVERSATION_DETAIL_SUCCESS)
          // console.log("lastedInviteMessageInReducer", lastedInviteMessageInReducer)
          const byId = { ...state.byId };
          delete byId[lastedInviteMessageInReducer.id];
          state = state.set('byId', byId);

          const chatData = state[type] && state[type][chatId] ? state[type][chatId].asMutable() : [];
          state = state.setIn(
            [type, chatId],
            chatData.filter(msgId => msgId !== lastedInviteMessageInReducer!.id)
          );
        }

        if (messageInvite && (!lastedInviteMessageInReducer || messageInvite?.time! > lastedInviteMessageInReducer.time)) {
          if (isInviteGroupMessage(chatMessage)) {
            lastedMessageInviteGroupChatObject[messageInvite.body.data.groupId] = messageInvite;
          } else if (isShareEventMessage(chatMessage)) {
            lastedMessageShareEventObject[messageInvite.body.data.id] = messageInvite;
          }
        }
      }

      if (!chatMessage.from && loginId && actionType === types.CHAT_ADD_SEND_MESSAGE_SUCCESS) {
        chatMessage.from = loginId;
      }

      if (message.body.isPrivate === chatTypes.TYPE_PRIVATE_MSG) {
        chatMessage.to = (chatId && chatId.split('/')[1]) || '';
      }
      state = state.setIn(['byId', id], chatMessage);
      if (message.body.isPrivate && message.body.isPrivate === chatTypes.TYPE_PRIVATE_MSG) {
        chatId = (chatId && chatId.split('/')[0]) || '';
      }
      if (type === 'end') {
        type = 'chat';
      }
      const chatData = state[type] && state[type][chatId] ? state[type][chatId].asMutable() : [];
      chatData.push(id);

      if (type === chatTypes.TYPE_GROUP_CHAT && chatData.length >= 200) {
        state = state.setIn([type, chatId], chatData.slice(50));
      } else {
        state = state.setIn([type, chatId], chatData);
      }
      if (message.refMsgShortDesc && message.refMsgShortDesc !== '') {
        chatMessage.refMsgShortDesc = message.refMsgShortDesc;
      }
      if (message.refMsgTitle) {
        chatMessage.refMsgTitle = message.refMsgTitle;
      }
      if (message.body && message.body.type === messageTypes.TYPE_TEXT) {
        chatMessage.body.data = message.body.data;
      }
      if (type === chatTypes.TYPE_GROUP_CHAT) {
        const groupActionObject = {
          type: actionType,
          message: chatMessage,
        };
        state = state.set('groupAction', groupActionObject);
      } else {
        const actionObject = {
          type: actionType,
          message: chatMessage, // object message xmpp message object in byId
          lastedInviteMessageInReducer,
        };
        if (isActiveInviteMessageGroupChat(chatMessage) || isActiveMessageTicketLink(chatMessage)) {
          messagesInvitePrivateGroupChat.push(chatMessage);
        }
        state = state.set('action', actionObject).set('messagesInvitePrivateGroup', messagesInvitePrivateGroupChat);
      }
      return state
        .set('lastedMessageInviteGroupChatByUser', { ...lastedMessageInviteGroupChatByUser, [chatId]: lastedMessageInviteGroupChatObject })
        .set('lastedMessageShareEventByUser', { ...lastedMessageShareEventByUser, [chatId]: lastedMessageShareEventObject });
    }
    case types.CHAT_DELETE_PEER_ALL_MESSAGES: {
      const chatData: string[] = state.chat && state.chat[peerXmppId] ? state.chat[peerXmppId].asMutable() : [];
      if (chatData.length > 0) {
        state = state.set('byId', state.byId.without(...chatData));
        state = state.setIn(['chat', peerXmppId], []);
      }
      return state;
    }
    case types.UPDATE_REACTION_MESSAGE_SUCCESS: {
      const message = state.byId[mid];
      if (message) {
        const newMessage = message.asMutable({ deep: true });
        if (!newMessage.reactions) {
          newMessage.reactions = {};
        }
        if (!isClearReaction) {
          if (!newMessage.reactions[unicode]) {
            newMessage.reactions = {
              ...newMessage.reactions,
              [unicode]: {
                [userId]: true,
              },
            };
            state = state.setIn(['byId', mid, 'reactions', unicode], {
              [userId]: true,
            });
          } else if (!newMessage.reactions[unicode][userId]) {
            newMessage.reactions = {
              ...newMessage.reactions,
              [unicode]: {
                ...newMessage.reactions[unicode],
                [userId]: true,
              },
            };
            state = state.setIn(['byId', mid, 'reactions', unicode], {
              ...newMessage.reactions[unicode],
              [userId]: true,
            });
          }
        } else {
          newMessage.reactions = {
            ...newMessage.reactions,
            [unicode]: {
              ...newMessage.reactions[unicode],
              [userId]: false,
            },
          };
          state = state.setIn(['byId', mid, 'reactions', unicode], {
            ...newMessage.reactions[unicode],
            [userId]: false,
          });
        }
        const { type } = newMessage;
        if (type === TYPE_GROUP_CHAT) {
          const actionObject = {
            type: actionType,
            message: newMessage, // object _message xmpp _message object in byId,
            oldMessage: message,
            updatedTime: +new Date(),
          };
          state = state.setIn(['groupAction'], actionObject);
        } else {
          const actionObject = {
            type: actionType,
            message: newMessage, // object _message xmpp _message object in byId
            updatedTime: +new Date(),
          };
          state = state.setIn(['action'], actionObject);
        }
      }
      // console.log("unicode", unicode);
      // console.log("msgId", mid);
      // console.log("state", state);
      return state;
    }
    case types.LOAD_CONVERSATION_DETAIL_FAILURE:
      if (type === TYPE_GROUP_CHAT) {
        const actionObject = {
          type: actionType,
          message: {},
          oldMessage: message,
          updatedTime: +new Date(),
        };
        state = state.setIn(['groupAction'], actionObject);
      } else {
        const actionObject = {
          type: actionType,
          message: {}, // object _message xmpp _message object in byId
          updatedTime: +new Date(),
        };
        state = state.setIn(['action'], actionObject);
      }
      return state.set('isLoading', false).set('isError', true);
    case types.LOAD_CONVERSATION_DETAIL_PENDING:
      return state.set('isLoading', true);
    case types.LOAD_CONVERSATION_DETAIL_SUCCESS: {
      let isGroupMessage = false;
      const messagesInvitePrivateGroupChat = [...state.messagesInvitePrivateGroup];

      const lastedMessageInviteGroupChatByUser = state.lastedMessageInviteGroupChatByUser.asMutable({ deep: true }) || {};
      const lastedMessageShareEventByUser = state.lastedMessageShareEventByUser.asMutable({ deep: true }) || {};
      const lastedMessageInviteGroupChatObject = { ...(lastedMessageInviteGroupChatByUser[peerXmppId] || {}) };
      const lastedMessageShareEventObject = { ...(lastedMessageShareEventByUser[peerXmppId] || {}) };

      try {
        const lastedMessageFromServer = messages[messages.length - 1];
        console.log('lastedMessageFromServer', lastedMessageFromServer);
        if (lastedMessageFromServer?.type === TYPE_GROUP_CHAT) {
          isGroupMessage = true;
        }
        if (shouldRemoveOldMessages) {
          let msgIdsDeleted: string[] = [];

          const chat = { ...(isGroupMessage ? state.groupchat : state.chat) };
          const byId = { ...state.byId };
          const msgIds = chat[peerXmppId];
          if (msgIds && msgIds.length > 0) {
            msgIds.forEach(msgId => {
              // ignore isLocalMessage to keep local upload video file
              if (!byId[msgId]?.isLocalMessage) {
                // and remove all message which older than lasted message from server
                if (lastedMessageFromServer && byId[msgId]?.time < lastedMessageFromServer?.time) {
                  msgIdsDeleted.push(msgId);
                  delete byId[msgId];
                  state = state.set('byId', byId);
                }
              }
            });
          }
          if (msgIdsDeleted.length > 0) {
            chat[peerXmppId] = chat[peerXmppId].filter(mid => !msgIdsDeleted.includes(mid));
            state = state.set(`${isGroupMessage ? 'groupchat' : 'chat'}`, chat);
          }
          messages.forEach(message => {
            // put the message which changed (state, reactions) to reducer
            const { createdAt = '', user } = message;
            if (!message.status) {
              const newMessage = parseFromAPIServer(message, bodyType);
              if (newMessage) {
                message = newMessage;
              }
            }
            const { xmppId = '' } = user || {};
            const { type, id, to } = message;
            // info source: if the field from is empty, then assume message is sent by current user
            const from = message.from || xmppId;
            // current user: mark as send by self
            const bySelf = type === TYPE_GROUP_CHAT ? message.bySelf : from === xmppId;
            // group or chat room id: sent by self or not one-to-one chat, then it's receiver's ID or sender's ID

            const chatId = bySelf || type !== chatTypes.TYPE_CHAT ? to : from;

            // make sure the send time of msg is not greater than current time
            const msgSentTime = datetimeToTimeStamp(createdAt);

            let messageInvite: ChatMessage | undefined = undefined;
            let lastedInviteMessageInReducer: ChatMessage | undefined = undefined;
            if (isInviteGroupMessage(message)) {
              messageInvite = message;
              lastedInviteMessageInReducer = lastedMessageInviteGroupChatObject[messageInvite.body.data.groupId];
            } else if (isShareEventMessage(message)) {
              messageInvite = message;
              lastedInviteMessageInReducer = lastedMessageShareEventObject[messageInvite.body.data.id];
            }

            let shouldAddMessageInviteToReducer =
              isValidMessage(messageInvite) &&
              (!lastedInviteMessageInReducer ||
                datetimeToTimeStamp(messageInvite?.createdAt) > datetimeToTimeStamp(lastedInviteMessageInReducer.createdAt));

            if (message.body.type !== '') {
              if (isValidMessage(messageInvite)) {
                // compare with lasted invite message from reducer, if lastedInviteMessageInReducer is not exist OR message is newer than lastedInviteMessageInReducer, then we add it to reducer
                if (shouldAddMessageInviteToReducer) {
                  // console.log("Save messageInvite data:", messageInvite.body.data)
                  state = state.setIn(['byId', id], {
                    ...message,
                    bySelf,
                    time: msgSentTime,
                  });
                  if (messageInvite) {
                    if (isInviteGroupMessage(message)) {
                      lastedMessageInviteGroupChatObject[messageInvite.body.data.groupId] = messageInvite;
                    } else if (isShareEventMessage(message)) {
                      lastedMessageShareEventObject[messageInvite.body.data.id] = messageInvite;
                    }
                  }
                } else {
                  // else, we ignore this messageInvite and do not add it to reducer
                  // console.log("Ignore save messageInvite data:", messageInvite.body.data)
                }
              } else {
                state = state.setIn(['byId', id], {
                  ...message,
                  bySelf,
                  time: msgSentTime,
                });
              }
            }
            const key = type === 'chat' ? 'chat' : 'groupchat';

            const chatData: string[] = state[key] && state[key][chatId] ? state[key][chatId].asMutable() : [];
            if (chatData.indexOf(id) === -1 && message.body.type !== '') {
              if (isValidMessage(messageInvite)) {
                if (shouldAddMessageInviteToReducer) {
                  chatData.unshift(id);
                }
              } else {
                chatData.unshift(id);
              }
            }
            state = state.setIn([key, chatId], chatData);

            // Only add message invite, message ticket link to this array
            if (!isGroupMessage && (isActiveInviteMessageGroupChat(message) || isActiveMessageTicketLink(message))) {
              messagesInvitePrivateGroupChat.push(message);
            }
          });
        } else {
          messages.forEach(_message => {
            let message = _message;
            const { createdAt = '', user } = message;
            if (!message.status) {
              const newMessage = parseFromAPIServer(message, bodyType);
              if (newMessage) {
                message = newMessage;
              }
            }
            const { xmppId = '' } = user || {};
            const { type, id, to } = message;
            // info source: if the field from is empty, then assume message is sent by current user
            const from = message.from || xmppId;
            // current user: mark as send by self
            const bySelf = type === TYPE_GROUP_CHAT ? message.bySelf : from === xmppId;
            // group or chat room id: sent by self or not one-to-one chat, then it's receiver's ID or sender's ID

            const chatId = bySelf || type !== chatTypes.TYPE_CHAT ? to : from;

            // make sure the send time of msg is not greater than current time
            const msgSentTime = datetimeToTimeStamp(createdAt);

            let messageInvite = undefined;
            let lastedInviteMessageInReducer = undefined;
            if (isInviteGroupMessage(message)) {
              messageInvite = message;
              lastedInviteMessageInReducer = lastedMessageInviteGroupChatObject[messageInvite.body.data.groupId];
            } else if (isShareEventMessage(message)) {
              messageInvite = message;
              lastedInviteMessageInReducer = lastedMessageShareEventObject[messageInvite.body.data.id];
            }

            if (!state.byId[id] && message.body.type !== '') {
              if (isValidMessage(messageInvite)) {
                // compare with lasted invite message from reducer, if lastedInviteMessageInReducer is not exist OR message is newer than lastedInviteMessageInReducer, then we add it to reducer
                if (
                  !lastedInviteMessageInReducer ||
                  datetimeToTimeStamp(messageInvite?.createdAt) > datetimeToTimeStamp(lastedInviteMessageInReducer.createdAt)
                ) {
                  // console.log("Save messageInvite data:", messageInvite.body.data)
                  state = state.setIn(['byId', id], {
                    ...message,
                    bySelf,
                    time: msgSentTime,
                  });
                  if (messageInvite) {
                    if (isInviteGroupMessage(message)) {
                      lastedMessageInviteGroupChatObject[messageInvite.body.data.groupId] = messageInvite;
                    } else if (isShareEventMessage(message)) {
                      lastedMessageShareEventObject[messageInvite.body.data.id] = messageInvite;
                    }
                  }
                } else {
                  // else, we ignore this messageInvite and do not add it to reducer
                  // console.log("Ignore save messageInvite data:", messageInvite.body.data)
                }
              } else {
                state = state.setIn(['byId', id], {
                  ...message,
                  bySelf,
                  time: msgSentTime,
                });
              }
            }
            const key = type === 'chat' ? 'chat' : 'groupchat';

            const chatData = state[key] && state[key][chatId] ? state[key][chatId].asMutable() : [];
            if (chatData.indexOf(id) === -1 && message.body.type !== '') {
              chatData.unshift(id);
            }
            state = state.setIn([key, chatId], chatData);
            // Only add message invite, message ticket link to this array
            if (!isGroupMessage && (isActiveInviteMessageGroupChat(message) || isActiveMessageTicketLink(message))) {
              messagesInvitePrivateGroupChat.push(message);
            }
          });
        }
      } catch (e) {
        console.log(e);
      }
      if (isGroupMessage) {
        const actionObject = {
          type: actionType,
          message: {}, // object _message xmpp _message object in byId,
          oldMessage: {},
          updatedTime: +new Date(),
        };
        state = state.setIn(['groupAction'], actionObject);
      } else {
        const actionObject = {
          type: actionType,
          message: {}, // object _message xmpp _message object in byId,
          oldMessage: {},
          updatedTime: +new Date(),
        };
        state = state.setIn(['action'], actionObject);
      }
      return state
        .set('isLoading', false)
        .set('isError', false)
        .set('lastedMessageInviteGroupChatByUser', {
          ...lastedMessageInviteGroupChatByUser,
          [peerXmppId]: lastedMessageInviteGroupChatObject,
        })
        .set('lastedMessageShareEventByUser', { ...lastedMessageShareEventByUser, [peerXmppId]: lastedMessageShareEventObject })
        .set('messagesInvitePrivateGroup', messagesInvitePrivateGroupChat);
    }
    case types.GET_PHOTOS_SUCCESS: {
      if (state.byId && state.byId[mid]) {
        const newMessage = cloneDeep(state.byId[mid]);
        // newMessage.body.data.isGranted = true;
        newMessage.body.data.items = { time: new Date(), data: imageData };
        newMessage.body.data.isLoadedData = true;
        state = state.setIn(['byId', mid], newMessage);
        const actionObject = {
          type: actionType,
          message: newMessage, // object _message xmpp _message object in byId
        };
        state = state.setIn(['action'], actionObject);
      }
      return state;
    }

    case types.UPDATE_USER_GROUPCHAT_MESSAGE: {
      const updateUser = user;
      const data = state.byId.asMutable() || [];
      let updateMessage = null;
      Object.keys(data).forEach((key, index) => {
        if (data[key].type === CHAT_TYPES.GROUP_CHAT && data[key].from === user.xmppId) {
          updateMessage = data[key];
          updateMessage = { ...updateMessage, user: updateUser };
          data[key] = updateMessage;
        }
      });
      return state.set('byId', data);
    }

    case types.GET_PHOTOS_DENIED: {
      if (state.byId[mid]) {
        const newMessage = state.byId[mid].asMutable({ deep: true });
        // newMessage.body.data.isGranted = false;
        newMessage.body.data.items = { time: new Date(), data: [] };
        state = state.setIn(['byId', mid], newMessage);
        const actionObject = {
          type: types.CHAT_UPDATE_MESSAGE_STATUS,
          message: newMessage, // object _message xmpp _message object in byId
        };
        state = state.setIn(['action'], actionObject);
      }
      return state;
    }
    case types.GROUPCHAT_DELETE_ALL_MESSAGES: {
      let arrIdMsg = state.byId && Object.keys(state.byId);
      const arrIdGroupChat = state.groupchat[fromJid] || [];
      arrIdMsg = arrIdMsg.filter(item => arrIdGroupChat.indexOf(item) === -1);
      if (arrIdMsg.length > 0) {
        const byId: Record<string, ChatMessage> = {};
        arrIdMsg.forEach(id => {
          byId[id] = state.byId[id].asMutable({ deep: true });
        });
        state = state.setIn(['byId'], byId);
      } else {
        state = state.setIn(['byId'], {});
      }
      state = state.setIn(['groupchat'], {});
      return state;
    }
    case types.USER_LOGOUT_SUCCESS: {
      state = initialState.message;
      return state;
    }

    case types.RESET_SELECTED_PEERVCARD_AND_CONVERSATION_INFO_SUCCESS: {
      state = state.setIn(['action'], initialState.message.action).setIn(['profilePhotoCommentShouldBeSend'], null);
      return state;
    }

    case types.UPDATE_MESSAGE_SENT:
      const _message = state.byId[mid];
      if (_message) {
        state = state.setIn(['byId', mid, 'sent'], true).setIn(['byId', mid, 'status'], messageStatus.STATUS_SENT);
      }
      return state;

    case types.REMOVE_STATUS_ERROR_MESSAGE: {
      const _message = state.byId[mid];
      // console.log("CHAT_REMOVE_MESSAGE_ERROR", mid, _message);
      if (_message && _message.error) {
        // Hide message by set field hidden = true
        state = state.setIn(['byId', mid, 'error'], false).setIn(['byId', mid, 'isHiddenByError'], false);

        const actionMessage = { ..._message, isHiddenByError: false, error: false, hidden: false };

        const actionObject = {
          type: actionType,
          message: actionMessage, // object _message xmpp _message object in byId
        };
        // console.log("actionObject", actionObject);
        if (_message.type === chatTypes.TYPE_GROUP_CHAT) {
          state = state.setIn(['groupAction'], actionObject);
        } else {
          state = state.setIn(['action'], actionObject);
        }
        return state;
      }
      return state;
    }

    case types.CHAT_REMOVE_MESSAGE_ERROR: {
      const _message = state.byId[mid];
      // console.log("CHAT_REMOVE_MESSAGE_ERROR", mid, _message);
      if (_message) {
        const toXmppId = _message.to;

        if (state.byId[mid]) {
          // Hide message by set field hidden = true
          state = state.setIn(['byId', mid, 'isHiddenByError'], true);
        }

        const actionMessage = { ..._message, isHiddenByError: true };
        actionMessage.hidden = true;

        const actionObject = {
          type: actionType,
          message: actionMessage, // object _message xmpp _message object in byId
        };
        // console.log("actionObject", actionObject);
        if (_message.type === chatTypes.TYPE_GROUP_CHAT) {
          state = state.setIn(['groupAction'], actionObject);
        } else {
          state = state.setIn(['action'], actionObject);
        }
        return state;
      }
      return state;
    }

    case types.EXPIRE_MESSAGE_SUCCESS: {
      const _message = state.byId[mid];
      if (_message) {
        state = state.setIn(['byId', mid, 'expts'], 1);

        const actionMessage = { ..._message };
        actionMessage.expts = 1;

        const actionObject = {
          type: actionType,
          message: actionMessage, // object _message xmpp _message object in byId
        };
        state = state.setIn(['action'], actionObject);
        return state;
      }
      return state;
    }

    case types.REMOVE_MESSAGE_SUCCESS: {
      const byId = { ...state.byId };
      const oldMessage = state.byId[mid];
      delete byId[mid];
      state = state.set('byId', byId);
      if (oldMessage) {
        const actionObject = {
          type: actionType,
          message: {}, // object _message xmpp _message object in byId
          oldMessage: oldMessage,
          updatedTime: +new Date(),
        };

        if (oldMessage.type === TYPE_GROUP_CHAT) {
          const roomJid = oldMessage.to;
          const groupchat = { ...state.groupchat };
          groupchat[roomJid] = (groupchat[roomJid] || []).filter(id => id !== mid);
          return state.set('groupchat', groupchat).set('groupAction', actionObject);
        } else {
          const chat = { ...state.chat };
          chat[oldMessage.to] = (chat[oldMessage.to] || []).filter(id => id !== mid);
          return state.set('chat', chat).set('action', actionObject);
        }
      }
      return state;
    }

    case types.REMOVE_MESSAGE_BY_ROOM_JID_SUCCESS: {
      const msgIds = state.groupchat[roomJid];
      const groupchat = { ...state.groupchat };
      const byId = { ...state.byId };
      if (msgIds && msgIds.length > 0) {
        msgIds.forEach(msgId => {
          delete byId[msgId];
        });
      }
      state = state.set('byId', byId);
      delete groupchat[roomJid];
      state = state.set('groupchat', groupchat);
      return state;
    }

    case types.MARK_EXPIRED_MESSAGE_INVITE_PRIVATE_GROUP_CHAT: {
      let messagesInvitePrivateGroupChat = [...state.messagesInvitePrivateGroup];
      //      const {roomJid} = action;
      messagesInvitePrivateGroupChat = messagesInvitePrivateGroupChat.filter(m => {
        if (roomJid && roomJid === m.body?.data?.roomJid) {
          state = state.setIn(['byId', m.id], { ...m, expts: 1 });
          return false;
        }
        return true;
      });
      return state.set('messagesInvitePrivateGroup', messagesInvitePrivateGroupChat);
    }

    case types.REMOVE_MESSAGE_BY_USER_ID_AND_ROOM_JID_SUCCESS: {
      // remove message, replied message, reactions
      const groupchat = { ...state.groupchat };
      const byId = state.byId.asMutable({ deep: true });
      let msgIdsInRoom = groupchat[roomJid];
      if (msgIdsInRoom && msgIdsInRoom.length > 0) {
        msgIdsInRoom = msgIdsInRoom.filter(msgId => {
          const msg = byId[msgId];
          const isGroupMessageStatus =
            msg.body?.type === TYPE_JOINED_GROUP_CHAT ||
            msg.body?.type === TYPE_LEAVE_GROUP_CHAT ||
            msg.body?.type === TYPE_REMOVED_FROM_GROUP;
          // We don't remove the group chat status message
          if (isGroupMessageStatus) return true;
          if (msg) {
            if (msg.user && msg.user.userId === userId) {
              delete byId[msgId];
              return false;
            }
            if (msg.refUserId === userId) {
              byId[msgId].refMsgIsDeleted = true;
            }
            // remove reactions by user
            byId[msgId] = removeUserReactionsFromMsg(msg, userId)!;
            // console.log("byId[msgId]", byId[msgId]);
            return true;
          }
          return false;
        });
      }
      state = state.set('byId', byId);
      groupchat[roomJid] = msgIdsInRoom;
      state = state.set('groupchat', groupchat);
      const actionObject = {
        type: actionType,
        message: {}, // object _message xmpp _message object in byId,
        oldMessage: {},
        updatedTime: +new Date(),
      };
      state = state.setIn(['groupAction'], actionObject);
      return state;
    }

    case types.DELETE_CONVERSATION_SUCCESS: {
      if (data) {
        const { isGroup, xmppId } = data;
        if (isGroup) {
          state = state.setIn(['groupchat', xmppId], []);
        }
      }
      return state;
    }

    case types.STORE_REACTION_MESSAGE_SUCCESS: {
      if (!message) {
        return state;
      }
      return state.setIn(['byId', message.id], message);
    }

    case types.UPDATE_PROFILE_PHOTO_MESSAGE: {
      return state.set('profilePhotoCommentShouldBeSend', message);
    }

    case types.CHAT_EDIT_TEXT_MESSAGE_SUCCESS: {
      const {
        id,
        body: { data },
        type,
      } = message;
      const messageShouldBeReplace = state.byId[id];
      if (messageShouldBeReplace?.id) {
        const newMessage = {
          ...messageShouldBeReplace,
          body: {
            ...messageShouldBeReplace.body,
            data,
          },
          edited: true,
        };
        if (type === chatTypes.TYPE_GROUP_CHAT) {
          const groupActionObject = {
            type: actionType,
            message: newMessage,
          };
          state = state.set('groupAction', groupActionObject);
        } else {
          const actionObject = {
            type: actionType,
            message: newMessage,
          };
          state = state.setIn(['action'], actionObject);
        }
        return state.setIn(['byId', id], newMessage);
      }
      return state;
    }

    default:
      return state;
  }
}

function isValidMessage(message: ChatMessage | undefined): boolean {
  return message?.body?.data && message?.body?.type;
}

function isShareEventMessage(message: ChatMessage | undefined): boolean {
  return isValidMessage(message) && message && message.body.type === TYPE_SHARE_EVENT && message.body.data.id;
}
function isInviteGroupMessage(message: ChatMessage | undefined): boolean {
  return isValidMessage(message) && message && message.body.type === TYPE_INVITE_GROUP && message.body.data.groupId;
}
